import { processTankData, addGroupSection, addStabulationVieuxSumFormulas } from './tankDataUtils';

export const generateStabulationVieuxSheet = async (ws, lastOps, tankDataMap, volumeUnit, cellarsRef, tankMapping) => {
  const boldStyle = { font: { bold: true, name: 'Arial' } };
  const boldCenteredStyle = { 
    font: { bold: true, name: 'Arial' },
    alignment: { horizontal: 'center', vertical: 'middle' },
  };

  // Define constants for row indexing
  const HEADER_ROWS = 6;
  const TANK_DATA_START_ROW = HEADER_ROWS + 1;

  // Set column widths
  ws.columns = Array(18).fill({ width: 20 });

  // Add header rows
  ws.mergeCells('C1:F1');
  ws.getCell('C1').value = 'Rhum vieux dans des cuves en préparation de sortie';
  ws.getCell('C1').style = boldCenteredStyle;

  ws.mergeCells('D2:E2');
  ws.getCell('D2').value = 'Inventaire au : ' + new Date().toLocaleDateString('fr-FR');
  ws.getCell('D2').style = boldCenteredStyle;

  ws.mergeCells('D4:E4');
  ws.getCell('D4').value = 'TRADITIONNEL VIEUX';
  ws.getCell('D4').style = boldCenteredStyle;

  // Add column headers
  const headers = [
    'Code article As 400',
    'Cuverie AS 400',
    'CUVE N°',
    'Ref alcool',
    'AGE EN ANNEE',
    'GRADUATION',
    'VOLUME',
    'Q* (TAV)',
    'T',
    'Q (TAV à 20°C)',
    'VOLUME EFFECTIF (à 20°C)',
    'VOLUME A.P',
  ];

  // Add headers to row 6 with explicit styling
  headers.forEach((header, index) => {
    const cell = ws.getCell(6, index + 1);
    cell.value = header;
    cell.style = {
      font: { bold: true, name: 'Arial' },
      alignment: { horizontal: 'center', vertical: 'middle' }
    };
  });

  // Group tanks by groupName
  const sortedTankGroups = tankMapping.reduce((groups, mapping) => {
    if (mapping.groupName === 'TRAD VIEUX' || mapping.groupName === 'AGRI VIEUX') {
      const groupName = mapping.groupName;
      if (!groups.find(g => g.groupName === groupName)) {
        groups.push({
          groupName,
          tanks: []
        });
      }
      const group = groups.find(g => g.groupName === groupName);
      group.tanks.push({
        tankName: mapping.tankName,
        cuverieAS400: mapping.cuverieAS400,
        codeArticleAS400: mapping.codeArticleAS400,
        refAlcohol: mapping.refAlcohol,
        serviceDate: mapping.serviceDate
      });
    }
    return groups;
  }, []);

  // Process the manually sorted tanks into separate arrays per group
  const tankDataByGroup = sortedTankGroups.map(group => {
    return group.tanks.map(tank => {
      const tankId = Object.keys(tankDataMap).find(id => 
        String(tankDataMap[id].name) === String(tank.tankName)
      );
      if (tankId && tankDataMap[tankId]) {
        return {
          tankId,
          ...tankDataMap[tankId],
          cuverieAS400: tank.cuverieAS400,
          codeArticleAS400: tank.codeArticleAS400,
          serviceDate: tank.serviceDate,
          refAlcohol: tank.refAlcohol
        };
      }
      return null;
    }).filter(Boolean);
  });

  // Process all groups in sequence
  let currentRow = TANK_DATA_START_ROW;
  const tradVieuxStartRow = currentRow;

  const config = {
    showServiceDate: false,
    columnOrder: [
      'showCodeArticleAS400',
      'showCuverieAS400',
      'showName',
      'showRefAlcohol',
      'showAgeInMonth',
      'showMaxVolume',
      'showCapacity',
      'showHeight',
      'showVolume',
      'showAbvAtTemperature',
      'showTemperature',
      'showAbvAt20C',
      'showVolumeAt20C',
      'showPureAlcohol',
      'showServiceDate',
      'showFillPercentage',
      'showRemainingVolume'
    ]
  };
  
  // Process TRAD VIEUX (group 0)
  const tradVieuxDataPromises = tankDataByGroup[0].map((tankData, index) => 
    processTankData(tankData, index, lastOps, tankDataMap, volumeUnit, tradVieuxStartRow, cellarsRef, config)
  );
  const tradVieuxData = await Promise.all(tradVieuxDataPromises);
  
  tradVieuxData.forEach((tank, index) => {
    tank.row.forEach((value, colIndex) => {
      const cell = ws.getCell(tradVieuxStartRow + index, colIndex + 1);
      if (typeof value === 'object' && value !== null) {
        if (value.t === 's') {
          cell.value = value.v;
        } else if (value.t === 'n' && value.f) {
          cell.value = { formula: value.f };
        }
      } else {
        cell.value = value;
      }
    });
  });
  
  const lastTradVieuxRow = tradVieuxStartRow + tradVieuxData.length - 1;
  const tradVieuxSumRow = lastTradVieuxRow + 1;
  addStabulationVieuxSumFormulas(ws, tradVieuxStartRow, lastTradVieuxRow, tradVieuxSumRow, boldStyle);
  
  currentRow = tradVieuxSumRow + 1;

  // Add AGRI VIEUX section
  const agriVieuxStartRow = addGroupSection(ws, 'AGRICOLE VIEUX', currentRow, boldCenteredStyle) + 1;

  // Process AGRI VIEUX (group 1)
  const agriVieuxDataPromises = tankDataByGroup[1].map((tankData, index) => 
    processTankData(tankData, index, lastOps, tankDataMap, volumeUnit, agriVieuxStartRow, cellarsRef, config)
  );
  const agriVieuxData = await Promise.all(agriVieuxDataPromises);
  
  agriVieuxData.forEach((tank, index) => {
    tank.row.forEach((value, colIndex) => {
      const cell = ws.getCell(agriVieuxStartRow + index, colIndex + 1);
      if (typeof value === 'object' && value !== null) {
        if (value.t === 's') {
          cell.value = value.v;
        } else if (value.t === 'n' && value.f) {
          cell.value = { formula: value.f };
        }
      } else {
        cell.value = value;
      }
    });
  });
  
  const lastAgriVieuxRow = agriVieuxStartRow + agriVieuxData.length - 1;
  const agriVieuxSumRow = lastAgriVieuxRow + 1;
  addStabulationVieuxSumFormulas(ws, agriVieuxStartRow, lastAgriVieuxRow, agriVieuxSumRow, boldStyle);

  return ws;
}; 